import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Logger } from '@crt/sui';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { UserState } from '@app/auth/user.state';
import { BootstrapSuffix } from '@app/common.model';
import { EnvironmentService } from '@app/shared/environment.service';
import { LoginProviderService, TokenResponse } from '@app/shared/login-provider.service';
import { ToastService } from '@app/toasts/toast.service';

function parseErrorMessage(error: object | string | undefined): {
  message?: string;
  redirectTo?: unknown;
} {
  if (typeof error === 'string') {
    try {
      return JSON.parse(error);
    } catch {
      return {
        message: error,
      };
    }
  }
  if (!error) {
    return {
      message: 'Unknown error occurred',
    };
  }
  return error;
}

export const tokenGuard: CanActivateFn = (): Observable<UrlTree | boolean> => {
  const environmentService = inject(EnvironmentService);
  const http = inject(HttpClient);
  const loginProviderService = inject(LoginProviderService);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const userState = inject(UserState);

  const baseUrl = environmentService.crtAPI;
  const tokenVerifyUrl = `${baseUrl}${environmentService.currentLocation}`;

  return http.get<TokenResponse>(tokenVerifyUrl).pipe(
    tap((response) => loginProviderService.provider.set(response.contents.provider)),
    map(() => true),
    tap(() => userState.refreshCurrentUser$.next()),
    catchError((e) => {
      Logger.error("[token.guard.ts] Error in function 'parseErrorMessage'", { data: e });
      if (e instanceof HttpErrorResponse) {
        const error = parseErrorMessage(e.error);
        if (error.message) {
          toastService.show({
            type: BootstrapSuffix.Danger,
            title: 'Error',
            text: error.message,
          });
        }
        if (typeof error.redirectTo === 'string') {
          return of(error.redirectTo);
        }
      }
      return of(false);
    }),
    map((successOrRedirectTo: string | boolean) => {
      if (typeof successOrRedirectTo === 'string') {
        const url = new URL(successOrRedirectTo, baseUrl);
        window.location.href = `${url}`;
        return false;
      } else if (successOrRedirectTo) {
        return router.parseUrl(userState.consumeReturnTo());
      } else {
        return router.parseUrl('/u/login');
      }
    }),
  );
};
